type AutocompleteResult = {
    value: any,
    text: string,
    attrs: Map<string, string>
};

let _instances = new WeakMap<Element, Autocomplete>();
let _instancesMultiple = new WeakMap<Element, AutocompleteMultiple>();

type AutocompleteOptions = {
    "uri": string,
    "delay": number,
    "onSelect": (r: AutocompleteResult) => any,
    "strict": boolean, // If true: only result from autocomplete result is accepted, display "No result"
}

class Autocomplete {
    elContainer: HTMLElement;
    elSearch:    HTMLInputElement;
    elMenu:      HTMLElement;
    elValue:     HTMLInputElement;

    elNoResult:  HTMLElement;

    value: string = null;

    filled: boolean = false;
    cursor: number = null;
    results: Array<AutocompleteResult>;


    onSelect: (r: AutocompleteResult) => any;

    uri: string;
    delay: number;
    timer: number;

    strict: boolean;

    constructor(container: Element, opts: Partial<AutocompleteOptions> = {}) {
        this.elContainer  = container as HTMLInputElement;
        this.elSearch     = container.querySelector('input.autocomplete-input') as HTMLInputElement;
        this.elMenu       = container.querySelector('.autocomplete-menu') as HTMLInputElement;

        this.elNoResult   = container.querySelector('.autocomplete-menu-noresult') as HTMLElement;

        if (this.elContainer.hasAttribute('data-autocomplete-target')) {
            this.elValue = document.querySelector(this.elContainer.getAttribute('data-autocomplete-target'));
        }

        if (this.elValue && this.elValue.value) {
            this.value = this.elValue.value;
            this.filled = true;
        }

        this.uri          = opts.uri;
        this.delay        = opts.delay || 100;
        this.strict       = opts.strict || false;

        this.onSelect     = opts.onSelect || this.defaultOnSelect

        this.results      = [];

        this.init();

        _instances.set(container, this);
    }

    static instance(el: Element): Autocomplete|null {
        return _instances.get(el);
    }

    init() {
        this.elSearch.addEventListener('input', (e) => {
            const q = this.elSearch.value;

            this.search(q)
        })

        this.elSearch.addEventListener('keydown', (e: KeyboardEvent) => {
            if (e.key == "Backspace") {
                if (this.filled) {
                    this.filled = false;
                    this.value = null;

                    this.onSelect(null)
                }
            } else if (e.key == "ArrowUp") {
                if (this.results.length > 0) {
                    if (this.cursor !== null) {
                        this.cursor = this.cursor > 0 ? this.cursor - 1 : 0;
                    } else {
                        this.cursor = 0;
                    }

                    this.highlightIndex(this.cursor)
                } else {
                    this.cursor = null;
                }
            } else if (e.key == "ArrowDown") { // down
                if (this.cursor !== null) {
                    this.cursor = this.cursor + 1
                } else {
                    this.cursor = 0;
                }

                if (this.cursor > this.results.length - 1) {
                    this.cursor = null;
                }

                this.highlightIndex(this.cursor)

            } else if (e.key == "Enter") { // enter
                if (this.cursor !== null) {
                    e.preventDefault();

                    this.selectIndex(this.cursor);
                }
            }

        });

        this.elSearch.addEventListener('focus', (e) => {
            this.elContainer.classList.add("active");

            if (this.results.length) {
                this.openMenu();
            }
        });

        this.elMenu.addEventListener('click', (e) => {
            const el = (e.target as Element);

            let elSelected;

            if (el.matches(".autocomplete-menu-item")) {
                elSelected = el;
            } else if (el.matches(".autocomplete-menu-item *")) {
                elSelected = el.closest(".autocomplete-menu-item");
            }

            if (elSelected) {
                if (elSelected.hasAttribute('data-index')) {
                    let i = elSelected.getAttribute('data-index');

                    this.selectIndex(parseInt(i));
                } else {
                    this.filled = false;
                    this.value = null;
                    this.onSelect(null)
                }
            }
        })
    }

    setUrlParams(base: string, params: Map<string, string>): string {
        let url = new URL(base, window.location.href);

        let urlParams = url.searchParams;

        params.forEach((v: string, k: string) => {
            urlParams.set(k, v);
        });

        return url.toString();
    }

    search(q: string) {

        this.filled = false;

        if (q.length) {
            this.startLoading();

            this.throttle(async () => {
                const uri = this.setUrlParams(this.uri, new Map([
                    ["q", q],
                    ["_ts", Date.now().toString()],
                ]))

                const res = await fetch(uri)

                if (res.ok) {
                    const body = await res.json();

                    let results: Array<AutocompleteResult> = [];

                    body.list.forEach((el: any) => {
                        let attrsMap = new Map<string, string>();

                        if ("attrs" in el) {
                            for(let k in el.attrs) {
                                attrsMap.set(k, el.attrs[k]);
                            }
                        }

                        results.push({
                            "value" : el.value || '',
                            "text": el.text || '',
                            "attrs": attrsMap,
                        })
                    })

                    this.results = results;

                    this.displayResults();
                }

                this.endLoading()

            }, this.delay)
        } else {
            this.clearMenu();
        }

    }

    highlightIndex(i: number) {
        const all = this.elMenu.querySelectorAll(".autocomplete-menu-item");

        all.forEach((e, i) => {
            e.classList.remove('hover');
        });


        const el = this.elMenu.querySelector("[data-index='"+i+"']");

        if (el) {
            el.classList.add('hover');
        }
    }

    selectIndex(i: number) {
        const r = this.results[i];

        this.filled = true;
        this.value = r ? r.value : null; 

        this.onSelect(r);

        this.cursor  = null;
        this.results = [];
        this.closeMenu();
    }

    defaultOnSelect(r: AutocompleteResult) {
        this.elSearch.value = r ? r.text : "";

        if (this.elValue) {
            const oldValue = this.elValue.value;
            const newValue = r ? r.value : "";


            if (oldValue != newValue) {
                this.elValue.value = newValue;
                this.elValue.dispatchEvent(new Event("change"))

                let toDelete = new Set<string>();

                for (let i = 0; i < this.elValue.attributes.length; i++) {
                    var attr = this.elValue.attributes[i];
                    if (attr.name.startsWith("data-")) {
                        toDelete.add(attr.name);
                    }
                }

                toDelete.forEach((k) => {
                    this.elValue.removeAttribute(k);
                })

                r.attrs.forEach((v, k) => {
                    this.elValue.setAttribute(k, v);
                })
            }
        }
    }

    openMenu() {
        this.elMenu.classList.add('open');
    }

    closeMenu() {
        this.elMenu.classList.remove('open');
    }

    clearMenu() {
        this.elMenu.classList.remove('open');
        this.elMenu.innerHTML = "";
    }

    displayResults() {

        this.clearMenu();

        this.results.forEach((e, i) => {
            const li   = document.createElement("li");
            li.classList.add('autocomplete-menu-item')
            li.setAttribute('data-index', i+"")
            li.setAttribute('data-value', e.value+"")

            e.attrs.forEach((v, k) => {
                li.setAttribute(k, v);
            })

            const text = document.createTextNode(e.text);
            li.appendChild(text);

            this.elMenu.appendChild(li);
        });

        if (this.results.length == 0 && this.elNoResult) {

            const li   = document.createElement("li");
            li.classList.add('autocomplete-menu-item');
            li.classList.add('autocomplete-menu-item-noresult');

            li.innerHTML = this.elNoResult.innerHTML;


            this.elMenu.appendChild(li);
        }

        this.openMenu();

    }

    afterSearch() {
        // Called when search is over
        this.elContainer.classList.remove("active");
        this.closeMenu();

        if (this.strict && !this.filled) {
            this.elSearch.value = "";
        }
    }

    startLoading() {

    }

    endLoading() {

    }

    throttle(f: () => void, delay: number) {
        clearTimeout(this.timer);

        this.timer = window.setTimeout(f, delay);
    }
}

class AutocompleteMultiple extends EventTarget {
    elContainer: HTMLElement;
    autocomplete: Autocomplete;

    elTemplate:    HTMLElement;
    elResults:     HTMLElement;

    filled: boolean = false;

    constructor(container: Element, opts: Partial<AutocompleteOptions> = {}) {
        super();

        this.elContainer  = container as HTMLInputElement;

        this.elTemplate = container.querySelector(".autocomplete-multi-template > li");
        this.elResults  = container.querySelector(".autocomplete-multi-values");

        const acOpts = opts;

        acOpts.onSelect = (r: AutocompleteResult) => {
            this.autocomplete.elSearch.value = "";

            const v = (this.elTemplate.cloneNode(true) as Element);

            const vInput  = (v.querySelector(".autocomplete-value-input") as HTMLInputElement);
            const vText   = (v.querySelector(".autocomplete-value-text") as HTMLElement);
            const vDelete = (v.querySelector(".autocomplete-value-delete") as HTMLElement);

            vInput.removeAttribute("disabled");
            vInput.value = r.value;

            vText.textContent = r.text;

            r.attrs.forEach((v, k) => {
                vInput.setAttribute(k, v);
            })

            vDelete.addEventListener("click", (e) => {
                v.remove();

                this.dispatchEvent(new Event("change"))
            });

            this.elResults.appendChild(v);
            this.dispatchEvent(new Event("change"))
        };

        this.autocomplete = new Autocomplete(container, acOpts);

        this.init();

        _instancesMultiple.set(container, this);
    }

    value(): Array<string> {
        let values: Array<string> = [];

        this.elResults.querySelectorAll(".autocomplete-multi-value .autocomplete-value-input").forEach((i: HTMLInputElement) => {
            values.push(i.value)
        })

        return values;
    }

    static instance(el: Element): AutocompleteMultiple|null {
        return _instancesMultiple.get(el);
    }

    init() {
        this.elContainer.querySelectorAll(".autocomplete-multi-values .autocomplete-multi-value .autocomplete-value-delete").forEach((d) => {
            d.addEventListener("click", (e) => {
                d.closest(".autocomplete-multi-value").remove();
                this.dispatchEvent(new Event("change"))
            });
        })
    }
}

// Close dropdowns
window.addEventListener('click', (e) => {
  const el = (e.target as Element);

  let thisAutocomplete: Autocomplete|null = null;

  if (el.matches(".autocomplete *")) {
    thisAutocomplete = Autocomplete.instance(el.closest(".autocomplete"));
  }

  // Close all other autocompletes
  const acs = document.querySelectorAll(".autocomplete.active");

  for (let i = 0; i < acs.length; i++) {
    const ac = Autocomplete.instance(acs[i]);

    if (ac !== thisAutocomplete) {
      ac.afterSearch();
    }
  }
})

export { Autocomplete, AutocompleteMultiple, AutocompleteResult };